import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { LenderTag, DisbursementTranche } from "@/types/deals";
import { CalendarIcon, Building2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface AddDisbursementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lenders: LenderTag[];
  disbursements: DisbursementTranche[];
  onSubmit: (disbursement: Omit<DisbursementTranche, "id">) => void;
  isHybrid?: boolean;
}

export function AddDisbursementDialog({
  open,
  onOpenChange,
  lenders,
  disbursements,
  onSubmit,
  isHybrid = false,
}: AddDisbursementDialogProps) {
  const [selectedLender, setSelectedLender] = useState<string>("");
  const [disbursedAmount, setDisbursedAmount] = useState<string>("");
  const [disbursedOn, setDisbursedOn] = useState<Date>();
  const [rentalScheduleNumber, setRentalScheduleNumber] = useState<string>("");
  const [lenderPF, setLenderPF] = useState<string>("");
  const [dueDate, setDueDate] = useState<Date>();
  // Leasing fields
  const [monthlyRental, setMonthlyRental] = useState<string>("");
  const [numberOfRentals, setNumberOfRentals] = useState<string>("");
  const [interestRate, setInterestRate] = useState<string>("");
  const [rentalFrequency, setRentalFrequency] = useState<"Monthly" | "Quarterly">("Monthly");
  const [fundingMode, setFundingMode] = useState<"Pre GST" | "Post GST">("Pre GST");
  // Hybrid payout fields
  const [payoutBasis, setPayoutBasis] = useState<"sanctioned" | "disbursed" | "manual">("sanctioned");
  const [customerPayoutPercent, setCustomerPayoutPercent] = useState<string>("");
  const [lenderPayoutPercent, setLenderPayoutPercent] = useState<string>("");
  const [partnerPayoutPercent, setPartnerPayoutPercent] = useState<string>("");
  const [customerPayoutAmount, setCustomerPayoutAmount] = useState<string>("");
  const [lenderPayoutAmount, setLenderPayoutAmount] = useState<string>("");
  const [partnerPayoutAmount, setPartnerPayoutAmount] = useState<string>("");

  // Filter lenders that have sanctions
  const sanctionedLenders = useMemo(() => {
    return lenders.filter((l) =>
      l.statusHistory.some(
        (e) =>
          (e.status === "Sanctioned" || e.status === "Disbursed") &&
          typeof e.sanctionAmount === "number" &&
          e.sanctionAmount > 0
      )
    );
  }, [lenders]);

  // Get sanction amount for selected lender
  const sanctionAmount = useMemo(() => {
    if (!selectedLender) return 0;
    const lender = lenders.find((l) => l.id === selectedLender);
    if (!lender) return 0;
    const sanctionEntry = [...lender.statusHistory]
      .reverse()
      .find((e) => typeof e.sanctionAmount === "number");
    return sanctionEntry?.sanctionAmount ?? 0;
  }, [selectedLender, lenders]);

  // Calculate available limit for selected lender
  const availableLimit = useMemo(() => {
    if (!selectedLender) return 0;
    const utilized = disbursements
      .filter((d) => d.lenderId === selectedLender)
      .reduce((sum, d) => sum + d.disbursedAmount, 0);
    return Math.max(0, sanctionAmount - utilized);
  }, [selectedLender, sanctionAmount, disbursements]);

  // Calculate base amount for percentage calculations
  const baseAmount = useMemo(() => {
    if (payoutBasis === "sanctioned") return sanctionAmount;
    if (payoutBasis === "disbursed") return parseFloat(disbursedAmount) || 0;
    return 0;
  }, [payoutBasis, sanctionAmount, disbursedAmount]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);

  const resetForm = () => {
    setSelectedLender("");
    setDisbursedAmount("");
    setDisbursedOn(undefined);
    setRentalScheduleNumber("");
    setLenderPF("");
    setDueDate(undefined);
    setMonthlyRental("");
    setNumberOfRentals("");
    setInterestRate("");
    setRentalFrequency("Monthly");
    setFundingMode("Pre GST");
    setPayoutBasis("sanctioned");
    setCustomerPayoutPercent("");
    setLenderPayoutPercent("");
    setPartnerPayoutPercent("");
    setCustomerPayoutAmount("");
    setLenderPayoutAmount("");
    setPartnerPayoutAmount("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const amount = parseFloat(disbursedAmount);
    if (!selectedLender || !disbursedOn || !dueDate || isNaN(amount) || amount <= 0) {
      return;
    }

    if (amount > availableLimit) {
      alert("Disbursed amount cannot exceed available limit");
      return;
    }

    const disbursementData: Omit<DisbursementTranche, "id"> = {
      lenderId: selectedLender,
      disbursedAmount: amount,
      disbursedOn: disbursedOn.toISOString(),
      rentalScheduleNumber: rentalScheduleNumber || `RS-${Date.now()}`,
      lenderPF: parseFloat(lenderPF) || 0,
      dueDate: dueDate.toISOString(),
      monthlyRental: parseFloat(monthlyRental) || 0,
      numberOfRentals: parseInt(numberOfRentals) || 12,
      interestRate: parseFloat(interestRate) || 0,
      rentalFrequency: rentalFrequency,
      fundingMode: fundingMode,
      createdAt: new Date().toISOString(),
    };

    // Add Hybrid-specific payout fields
    if (isHybrid) {
      disbursementData.payoutBasis = payoutBasis;
      if (payoutBasis === "manual") {
        disbursementData.customerPayoutAmount = parseFloat(customerPayoutAmount) || 0;
        disbursementData.lenderPayoutAmount = parseFloat(lenderPayoutAmount) || 0;
        disbursementData.partnerPayoutAmount = parseFloat(partnerPayoutAmount) || 0;
      } else {
        disbursementData.customerPayoutPercent = parseFloat(customerPayoutPercent) || 0;
        disbursementData.lenderPayoutPercent = parseFloat(lenderPayoutPercent) || 0;
        disbursementData.partnerPayoutPercent = parseFloat(partnerPayoutPercent) || 0;
        disbursementData.customerPayoutAmount = (parseFloat(customerPayoutPercent) || 0) / 100 * baseAmount;
        disbursementData.lenderPayoutAmount = (parseFloat(lenderPayoutPercent) || 0) / 100 * baseAmount;
        disbursementData.partnerPayoutAmount = (parseFloat(partnerPayoutPercent) || 0) / 100 * baseAmount;
      }
    }

    onSubmit(disbursementData);
    resetForm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Disbursement Tranche</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Lender Selection */}
          <div className="space-y-2">
            <Label>Select Lender *</Label>
            <Select value={selectedLender} onValueChange={setSelectedLender}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a sanctioned lender" />
              </SelectTrigger>
              <SelectContent>
                {sanctionedLenders.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      <span>{l.institution}</span>
                      <span className="text-muted-foreground">- {l.lenderName}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedLender && (
              <div className="text-xs text-muted-foreground space-y-1">
                <p>Sanctioned: <span className="font-medium text-blue-600">{formatCurrency(sanctionAmount)}</span></p>
                <p>Available Limit: <span className="font-medium text-green-600">{formatCurrency(availableLimit)}</span></p>
              </div>
            )}
          </div>

          {/* Disbursed Amount */}
          <div className="space-y-2">
            <Label>Disbursed Amount (₹) *</Label>
            <Input
              type="number"
              placeholder="Enter amount"
              value={disbursedAmount}
              onChange={(e) => setDisbursedAmount(e.target.value)}
              min={0}
              max={availableLimit}
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Disbursed On *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-full justify-start text-left font-normal", !disbursedOn && "text-muted-foreground")}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {disbursedOn ? format(disbursedOn, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={disbursedOn}
                    onSelect={setDisbursedOn}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Due Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-full justify-start text-left font-normal", !dueDate && "text-muted-foreground")}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? format(dueDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={setDueDate}
                    disabled={(date) => disbursedOn ? date < disbursedOn : false}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Schedule Number & PF */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Rental Schedule No.</Label>
              <Input
                placeholder="e.g., RS-2026-001"
                value={rentalScheduleNumber}
                onChange={(e) => setRentalScheduleNumber(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Lender PF (₹)</Label>
              <Input
                type="number"
                placeholder="Processing fee"
                value={lenderPF}
                onChange={(e) => setLenderPF(e.target.value)}
                min={0}
              />
            </div>
          </div>

          {/* Conditional: Leasing - Rental Structure */}
          {!isHybrid && (
            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-3 block">Rental Structure</Label>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="space-y-2">
                  <Label className="text-xs">Monthly Rental (₹)</Label>
                  <Input
                    type="number"
                    placeholder="Amount"
                    value={monthlyRental}
                    onChange={(e) => setMonthlyRental(e.target.value)}
                    min={0}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">No. of Rentals</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 12"
                    value={numberOfRentals}
                    onChange={(e) => setNumberOfRentals(e.target.value)}
                    min={1}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Interest Rate (%)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 14.5"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                    min={0}
                    step={0.1}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Rental Frequency</Label>
                  <Select value={rentalFrequency} onValueChange={(v) => setRentalFrequency(v as "Monthly" | "Quarterly")}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Funding Mode</Label>
                  <Select value={fundingMode} onValueChange={(v) => setFundingMode(v as "Pre GST" | "Post GST")}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pre GST">Pre GST</SelectItem>
                      <SelectItem value="Post GST">Post GST</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Conditional: Hybrid - Payout Details */}
          {isHybrid && (
            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-3 block">Payout Details (DSA Mode)</Label>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs">Payout Basis</Label>
                  <RadioGroup value={payoutBasis} onValueChange={(v) => setPayoutBasis(v as typeof payoutBasis)} className="flex gap-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="sanctioned" id="sanctioned" />
                      <Label htmlFor="sanctioned" className="text-xs cursor-pointer">% on Sanctioned</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="disbursed" id="disbursed" />
                      <Label htmlFor="disbursed" className="text-xs cursor-pointer">% on Disbursed</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="manual" id="manual" />
                      <Label htmlFor="manual" className="text-xs cursor-pointer">Manual Entry</Label>
                    </div>
                  </RadioGroup>
                </div>

                {payoutBasis !== "manual" && (
                  <>
                    <p className="text-xs text-muted-foreground">
                      Base Amount: <span className="font-medium">{formatCurrency(baseAmount)}</span>
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs">Customer Payout (%)</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 0.5"
                          value={customerPayoutPercent}
                          onChange={(e) => setCustomerPayoutPercent(e.target.value)}
                          step={0.01}
                        />
                        <p className="text-xs text-muted-foreground">
                          = {formatCurrency((parseFloat(customerPayoutPercent) || 0) / 100 * baseAmount)}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Lender Payout (%)</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 0.75"
                          value={lenderPayoutPercent}
                          onChange={(e) => setLenderPayoutPercent(e.target.value)}
                          step={0.01}
                        />
                        <p className="text-xs text-muted-foreground">
                          = {formatCurrency((parseFloat(lenderPayoutPercent) || 0) / 100 * baseAmount)}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Partner Payout (%)</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 0.25"
                          value={partnerPayoutPercent}
                          onChange={(e) => setPartnerPayoutPercent(e.target.value)}
                          step={0.01}
                        />
                        <p className="text-xs text-muted-foreground">
                          = {formatCurrency((parseFloat(partnerPayoutPercent) || 0) / 100 * baseAmount)}
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {payoutBasis === "manual" && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Customer Payout (₹)</Label>
                      <Input
                        type="number"
                        placeholder="Amount"
                        value={customerPayoutAmount}
                        onChange={(e) => setCustomerPayoutAmount(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Lender Payout (₹)</Label>
                      <Input
                        type="number"
                        placeholder="Amount"
                        value={lenderPayoutAmount}
                        onChange={(e) => setLenderPayoutAmount(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Partner Payout (₹)</Label>
                      <Input
                        type="number"
                        placeholder="Amount"
                        value={partnerPayoutAmount}
                        onChange={(e) => setPartnerPayoutAmount(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!selectedLender || !disbursedOn || !dueDate || !disbursedAmount}>
              Add Disbursement
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
